﻿import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { NotificationRecipientItem } from '../../services/notifications'
import { Button } from '../ui/button'
import { renderTextWithLinks } from './notificationTextLinks'
import { formatUserNameWithDepartment } from '../../utils/userDisplayName'

export interface NotificationDetailModalProps {
  notification: NotificationRecipientItem
  senderInfo: { name: string; avatarUrl: string | null; departmentName?: string | null } | null
  loadingAvatar: boolean
  onClose: () => void
  onMarkAsRead?: (recipientId: string) => void | Promise<void>
  onDelete?: (recipientId: string) => void | Promise<void>
  onInternalNavigate?: (path: string) => void
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m atrás`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h atrás`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d atrás`
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

const getInitials = (text: string): string => {
  if (!text) return 'N'
  const words = text.trim().split(/\s+/)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return text.substring(0, 2).toUpperCase()
}

const getAvatarColor = (text: string): string => {
  const colors = [
    'bg-purple-500', 'bg-primary-500', 'bg-green-500', 'bg-yellow-500',
    'bg-pink-500', 'bg-indigo-500', 'bg-red-500', 'bg-teal-500',
  ]
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

/**
 * Modal único de "Ver mensagem completa" usado no Dashboard e no menu de Notificações (sidebar).
 * Renderizado em portal no body para ficar sempre por cima (z-[9999]).
 */
const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  notification,
  senderInfo,
  loadingAvatar,
  onClose,
  onMarkAsRead,
  onDelete,
  onInternalNavigate,
}) => {
  const [markingAsRead, setMarkingAsRead] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const isUnread = !notification.read_at
  const createdAt = notification.created_at || notification.notification?.created_at || ''

  const handleInternalNavigate = (path: string) => {
    onClose()
    onInternalNavigate?.(path)
  }

  const linkOptions = onInternalNavigate
    ? { onInternalNavigate: handleInternalNavigate }
    : undefined

  const handleMarkAsRead = async () => {
    if (!onMarkAsRead || !isUnread || markingAsRead) return
    setMarkingAsRead(true)
    try {
      await onMarkAsRead(notification.id)
    } finally {
      setMarkingAsRead(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete || deleting) return
    if (!window.confirm('Excluir esta notificação?')) return
    setDeleting(true)
    try {
      await onDelete(notification.id)
    } finally {
      setDeleting(false)
    }
  }

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-[900px] w-[70%] max-h-[90vh] overflow-y-auto focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-full ${
                loadingAvatar || !senderInfo
                  ? 'bg-gray-300 dark:bg-gray-600'
                  : senderInfo?.avatarUrl
                    ? ''
                    : getAvatarColor(senderInfo?.name || 'Usuário')
              } flex items-center justify-center text-white font-semibold text-sm overflow-hidden`}
            >
              {loadingAvatar || !senderInfo ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-300 dark:bg-gray-600 animate-pulse" />
              ) : senderInfo?.avatarUrl ? (
                <img
                  src={senderInfo.avatarUrl}
                  alt={senderInfo.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                getInitials(senderInfo?.name || 'Usuário')
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {notification.notification?.title || 'Notificação'}
              </h2>
              {(senderInfo?.name || loadingAvatar) && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                  {loadingAvatar ? (
                    <span className="inline-block w-24 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                  ) : (
                    <>
                      Enviado por <span className="font-medium">{formatUserNameWithDepartment(senderInfo?.name, senderInfo?.departmentName)}</span>
                    </>
                  )}
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {createdAt ? formatTimeAgo(createdAt) : 'Data não disponível'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Fechar modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mensagem
            </label>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                {renderTextWithLinks(notification.notification?.body || 'Sem conteúdo', linkOptions)}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            {onDelete ? (
              <Button
                variant="ghost"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
              >
                <TrashIcon className="h-4 w-4" />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              {isUnread && onMarkAsRead && (
                <Button
                  variant="solid"
                  onClick={handleMarkAsRead}
                  disabled={markingAsRead}
                  className="inline-flex items-center gap-1.5"
                >
                  <CheckIcon className="h-4 w-4" />
                  {markingAsRead ? 'Marcando...' : 'Marcar como lida'}
                </Button>
              )}
              <Button variant="ghost" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default NotificationDetailModal
